import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  Pagination,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";
import InvitedCandidate from "./invite-candidates";
import { useNavigate } from "react-router-dom";
import { paths } from "../routes/path";

const OpenPosition = ({ candidates }) => {
  const [positions, setPositions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCloneDialog, setOpenCloneDialog] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedPositionForCandidates, setSelectedPositionForCandidates] =
    useState(null);
  const [openCandidateHistoryDialog, setOpenCandidateHistoryDialog] =
    useState(false);
  const [candidateHistoryStatus, setCandidateHistoryStatus] = useState("");
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const showSnackbar = (msg, type) => {
    setMessage(msg);
    setSeverity(type);
    setOpen(true);
  };

  useEffect(() => {
    fetchPositions();
  }, [searchTerm, sortBy]);

  const handlePageChange = (event, page) => {
    fetchPositions(page);
  };

  const fetchPositions = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.a2ajobs.com/api/position-list?page=${page}&search=${searchTerm}&sortby=${sortBy}`
      );
      setPositions(response.data);
      setTotalPages(response.data.data.last_page);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching positions:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (positionId) => {
    if (
      !window.confirm(
        "All its related data will be lost as well. Are you sure you want to delete this position?"
      )
    )
      return;

    setLoading(true);
    try {
      await axios.post(
        "https://api.a2ajobs.com/api/position-delete",
        { position_id: positionId },
        { headers: { "Content-Type": "application/json" } }
      );

      setPositions((prevPositions) => ({
        ...prevPositions,
        data: prevPositions.data.data.filter((pos) => pos.id !== positionId),
      }));
      fetchPositions();

      setLoading(false);
      showSnackbar("Position deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting position:", error);
      setLoading(false);
      showSnackbar("Failed to delete position", "error");
    }
  };
  const handleClosePosition = async () => {
    if (!selectedPosition) return;
    try {
      const response = await axios.post(
        "https://api.a2ajobs.com/api/position-close",
        { position_id: selectedPosition.id },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.status === 200) {
        setPositions((prev) => ({
          ...prev,
          data: prev.data.data.filter((pos) => pos.id !== selectedPosition.id),
        }));
        fetchPositions();
        showSnackbar("Position closed successfully!", "success");
      } else {
        alert("Failed to close position!");
      }
    } catch (error) {
      console.error("Error closing position:", error);
      alert("Something went wrong!");
    } finally {
      handleCloseDialog();
    }
  };

  const handleClonePosition = async () => {
    if (!selectedPosition) return;

    setIsCloning(true);
    try {
      const response = await axios.post(
        "https://api.a2ajobs.com/api/position-clone",
        { position_id: selectedPosition.id },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.status === 200) {
        showSnackbar("Position cloned successfully!", "success");
        fetchPositions();
      } else {
        alert("Failed to clone position!");
      }
    } catch (error) {
      console.error("Error cloning position:", error);
      alert("Something went wrong!");
    } finally {
      setOpenCloneDialog(false);
      setIsCloning(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPosition(null);
  };

  const handleOpenUpdateDialog = (position) => {
    setSelectedPosition(position);
    navigate(paths.addPosition, {
      state: { position },
    });
  };

  const handleOpenCandidateHistory = (position, status) => {
    setSelectedPositionForCandidates(position);
    setCandidateHistoryStatus(status);
    setOpenCandidateHistoryDialog(true);
  };

  const filteredPositions = positions?.data?.data?.filter((pos) =>
    pos.title?.toLowerCase().includes(searchTerm)
  );

  return (
    <Box
      sx={{
        maxWidth: "100%",
        margin: "auto",
        p: { md: 2, xs: 0 },
        width: "97%",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        mb={2}
        alignItems="center"
        justifyContent="flex-end"
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search positions here"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            height: "100%",
            ".MuiOutlinedInput-root": {
              height: "100%",
              borderRadius: "10px",
              backgroundColor: "common.grey",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchTerm("")} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          displayEmpty
          size="small"
          sx={{ bgcolor: "white", borderRadius: 2, minWidth: "120px" }}
        >
          <MenuItem value="newest">Newest First</MenuItem>
          <MenuItem value="oldest">Oldest First</MenuItem>
          <MenuItem value="a-z">A-Z</MenuItem>
          <MenuItem value="z-a">Z-A</MenuItem>
        </Select>
      </Stack>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={50} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredPositions?.length > 0 ? (
            filteredPositions?.map((pos, index) => {
              const positionCandidates = candidates.filter(
                (candidate) => candidate.position_id === pos.id
              );
              const invitedCount = positionCandidates.filter(
                (c) => c.status === "invited"
              ).length;
              const toEvaluateCount = positionCandidates.filter(
                (c) => c.status === "evaluate"
              ).length;
              const shortlistedCount = positionCandidates.filter(
                (c) => c.status === "shortlisted"
              ).length;
              const discardedCount = positionCandidates.filter(
                (c) => c.status === "discarded"
              ).length;

              return (
                <Grid item xs={12} sm={12} md={12} key={index}>
                  <Card
                    onClick={() => handleOpenUpdateDialog(pos)}
                    sx={{ "&:hover": { cursor: "pointer" } }}
                  >
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="h6">{pos.title}</Typography>

                          <Stack
                            direction="row"
                            spacing={1}
                            mt={1}
                            flexWrap="wrap"
                          >
                            <Chip
                              label={`Invited (${invitedCount})`}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCandidateHistory(pos, "invited");
                              }}
                            />
                            <Chip
                              label={`To Evaluate (${toEvaluateCount})`}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCandidateHistory(pos, "evaluate");
                              }}
                            />
                            <Chip
                              label={`Shortlisted (${shortlistedCount})`}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCandidateHistory(pos, "shortlisted");
                              }}
                            />
                            <Chip
                              label={`Discarded (${discardedCount})`}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCandidateHistory(pos, "discarded");
                              }}
                            />
                          </Stack>
                        </Box>

                        <Box>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(pos.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPosition(pos);
                                setOpenCloneDialog(true);
                              }}
                            >
                              <ContentCopyIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPosition(pos);
                                setOpenDialog(true);
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Typography variant="h5" align="center">
                No Data Found
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {!totalPages === currentPage && (
        <Stack mt={1}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Stack>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{ sx: { borderRadius: 5, p: 2 } }}
      >
        <DialogTitle>Close position</DialogTitle>
        <DialogContent>
          <Typography>
            Important: There's one candidate still in the "invited" stage. If
            you close this position they won't be able to complete their
            interview until you re-open the position again. Moreover, this
            position's public link is active. If you close this position, the
            public link won't work until you re-open the position again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ my: 1 }}>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            sx={{ borderRadius: 20, border: "1px solid", px: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClosePosition}
            color="error"
            variant="contained"
            sx={{ borderRadius: 20, px: 2 }}
          >
            Close this position
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCloneDialog}
        onClose={() => setOpenCloneDialog(false)}
        PaperProps={{ sx: { borderRadius: 5, p: 2 } }}
        fullWidth
      >
        <DialogTitle>Clone position</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to clone <strong>{selectedPosition?.title}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ my: 1 }}>
          <Button
            onClick={() => setOpenCloneDialog(false)}
            color="primary"
            sx={{ borderRadius: 20, border: "1px solid", px: 2 }}
            disabled={isCloning}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClonePosition}
            color="primary"
            variant="contained"
            disabled={isCloning}
            sx={{ borderRadius: 20, px: 2 }}
          >
            {isCloning ? "Cloning..." : "Clone position"}
          </Button>
        </DialogActions>
      </Dialog>
      {openCandidateHistoryDialog && selectedPositionForCandidates && (
        <InvitedCandidate
          open={openCandidateHistoryDialog}
          onClose={() => setOpenCandidateHistoryDialog(false)}
          position={selectedPositionForCandidates}
          questions={[]}
          initialTab={candidateHistoryStatus}
        />
      )}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={severity}
          variant="filled"
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OpenPosition;
